/**
 * Wrapper class for Froala, which initializes and upgrades Froala in several
 *  ways including autosaving to a hidden field, adding custom controls, and
 * providing easy binding for several common events.
 *
 * N2.handle is API relied upon by this class. The signiture of that method is
 * eventName, selector, callback, just like jQuery.on
 */

import Froala from "froala-editor";
import "froala-editor/js/plugins/lists.min";
import "froala-editor/js/plugins/char_counter.min";
import "froala-editor/js/plugins/word_paste.min";

import FroalaTextTransformPlugin from "./FroalaTextTransformPlugin";

class FroalaEditor {
  // Public API by Froala for customizing.
  static editor() {
    return $.FE;
  }

  constructor(selector = ".rich-text-editor", autosaveSelector = null) {
    this.selector = selector;
    this.autosaveSelector = autosaveSelector;
    if (!this.autosaveSelector) {
      this.autosaveSelector = this.selector;
    }
    this.$editor = $(this.selector);
    this.setButtons(["bold", "italic", "formatUL", "formatOL", "undo", "redo"]);
  }

  enableTextTransformPlugin() {
    new FroalaTextTransformPlugin();

    this.setButtons(["bold", "italic", "formatUL", "formatOL",
      "upcase", "downcase", "titlecase", "undo", "redo"]);
  }

  init() {
    N2.handle("froalaEditor.initialized, froalaEditor.charCounter.update",
      this.selector, this.formatCharCounter);

    // Auto save content changed in the rich text editor box back to the
    // associated field.
    N2.handle("froalaEditor.contentChanged", this.selector, (e, editor) => {
      this.autosave(editor.html.get());
    });

    this.editor = new Froala(this.selector, this.froalaOptions());
    this.setToolbarIcons();
  }

  // Froala functions as a contenteditable area that augments your input, after
  // hiding it. If configured, this will save any changes to text back to the
  // field of choice on any change event.
  autosave(richText) {
    const $target = $(this.autosaveSelector);

    if ($target.is("textarea")) {
      $target.text(richText);
    } else if ($target) {
      $target.val(richText);
    }
  }

  // Bind a handler for blur events on Froala.
  blurListener(cb) {
    N2.handle("froalaEditor.blur", this.selector, cb);
  }

  froalaOptions() {
    const opts = {
      attribution: false,
      toolbarButtons: this.buttons,
      shortcutsEnabled: this.buttons,
      theme: "n2-material",
      placeholderText: null,
      key: "eHE5C-11F2A2D1E2A5B4D-17d1F1FOOLb2KOPQGe1CWCQVTDWXGcA5A4D4B3E4C2C2E3B1A2==",
      language: "en_us",
      enter: Froala.ENTER_DIV,
      htmlAllowedTags: ["p", "br", "div", "strong", "em", "ul", "li", "ol"],
      htmlAllowedEmptyTags: [],
      htmlAllowedAttrs: [],

      // word_paste.js plugin overrides
      wordAllowedStyleProps: [], // no style properties allowed, we don't support them
      wordPasteModal: false, // no question, always clean pasted Word text
    };

    // If a maxLength is passed in, set it. Otherwise no char counter.
    const max = this.$editor.data("maxLength");
    if (max) {
      opts.charCounterMax = max;
    } else {
      opts.charCounterCount = false;
    }

    return opts;
  }

  // Manually trigger a focus event.
  focus() {
    this.editor("events.focus");
  }

  // Bind an event listener to the Froala's focus event.
  focusListener(cb) {
    N2.handle("froalaEditor.focus", this.selector, cb);
  }

  // Adjust styling via js because there isn't an easy way to style this with
  // CSS. Intended to turn "123/234" into "123 / 234" so we can adjust the word
  // spacing.
  formatCharCounter() {
    const $ele = $(".fr-counter", this.$editor);
    const text = $ele.html();

    if (text && (text.match("/"))) {
      const spl = text.split("/");
      $ele.html(`${spl[0]} / ${spl[1]}`);
    }
  }

  // Customize buttons used for a Froala editor.
  setButtons(buttonArray) {
    this.buttons = buttonArray;
  }

  setToolbarIcons() {
    // Declare our own custom icon template for material icons, and set it as
    // the default.
    Froala.DefineIconTemplate("n2styles", '<i class="material-icons">[NAME]</i>');
    Froala.ICON_DEFAULT_TEMPLATE = "n2styles";

    // Configure material icon names for each control we want to support. Note
    // that the custom-built buttons (upcase, etc) have their own setup for this
    // in the text_transform plugin.
    Froala.DefineIcon("bold", { NAME: "format_bold" });
    Froala.DefineIcon("italic", { NAME: "format_italic" });
    Froala.DefineIcon("formatUL", { NAME: "format_list_bulleted" });
    Froala.DefineIcon("formatOL", { NAME: "format_list_numbered" });
    Froala.DefineIcon("undo", { NAME: "undo" });
    Froala.DefineIcon("redo", { NAME: "redo" });

    // config.RegisterShortcut(key_code, cmd, val, letter, shift_key, option_key);
    // key_code  : key code of the key pressed
    // cmd       : command that should be triggered
    // val       : value passed to the command (optional)
    // letter    : the letter to be shown in the tooltip for shortcut (optional)
    // shift_key : shortcut needs to have the SHIFT key pressed (optional)
    // option_key: shortcut needs to have the OPTION key pressed (optional)

    // Ctrl + - for bulletted list
    Froala.RegisterShortcut(Froala.KEYCODE.DASH, "formatUL", null, "-", false);
    // Ctrl + 1 for numbered list
    Froala.RegisterShortcut(Froala.KEYCODE.ONE, "formatOL", null, "1", false);
  }
}

export default FroalaEditor;
